import auth from '@client/components/auth';
import {
  useGetAllProgramsQuery,
  useGetAllProgramsWithWorkoutsQuery,
  useGetProgramPublishedWorkoutsQuery,
} from '../../../../generated/graphql';
import { browserHistory } from '@client/router/compat';
import { ProgramPage } from './components/ProgramPage';
import { WorkoutsPage } from './components/WorkoutsPage';

export function PublishedWorkoutsPage(props) {
  const influencerId = Number(
    props.params.influencerId
      ? props.params.influencerId
      : auth.getUser()?.influencerId,
  );

  const programIdParam = props.params.programId;
  const urlProgramId =
    programIdParam !== undefined ? parseInt(programIdParam, 10) : null;
  // Library (id 0) shows the aggregate of every non-exclusive program's
  // workouts, so it needs the heavier list. It is never reached today
  // (hideLibrary is true for all creators) but kept correct as a hedge.
  const isLibrary = urlProgramId === 0;

  const url = new URL(window.location.href);
  const shouldRefresh = url.searchParams.get('refresh');
  const fetchPolicy = shouldRefresh ? 'network-only' : 'cache-and-network';

  const stripRefresh = () => {
    if (shouldRefresh) {
      url.searchParams.delete('refresh');
      browserHistory.replace(url.href);
    }
  };

  const listQuery = useGetAllProgramsQuery({
    variables: { id: influencerId },
    skip: !influencerId || isLibrary,
    fetchPolicy,
    onCompleted: stripRefresh,
  });

  const libraryQuery = useGetAllProgramsWithWorkoutsQuery({
    variables: { id: influencerId },
    skip: !influencerId || !isLibrary,
    fetchPolicy,
    onCompleted: stripRefresh,
  });

  const {
    loading: programsLoading,
    data,
    refetch: refetchPrograms,
    updateQuery,
  } = isLibrary ? libraryQuery : listQuery;

  const basePrograms = data?.creator ? data.creator.programs : [];
  // No id in the URL resolves to the first program (matches ProgramPage).
  const selectedProgramId =
    urlProgramId !== null ? urlProgramId : (basePrograms[0]?.id ?? null);
  const loadSingleProgram = selectedProgramId !== null && selectedProgramId > 0;

  const {
    loading: workoutsLoading,
    data: programWorkoutsData,
    refetch: refetchProgramWorkouts,
  } = useGetProgramPublishedWorkoutsQuery({
    variables: { programId: selectedProgramId ?? 0 },
    skip: !loadSingleProgram,
    fetchPolicy,
  });

  // Page-level loader covers only the initial program-list fetch, so the
  // program picker stays mounted while a program's workouts (re)load. Both
  // flags ignore the background revalidation cache-and-network triggers on top
  // of cached data - only a fetch with nothing to show yet is a loading state.
  const isListLoading = programsLoading && !data;
  const isWorkoutsLoading =
    loadSingleProgram && workoutsLoading && !programWorkoutsData;

  const specificWorkouts = programWorkoutsData?.program?.workouts ?? [];
  const programs = loadSingleProgram
    ? basePrograms.map((p) =>
        p.id === selectedProgramId ? { ...p, workouts: specificWorkouts } : p,
      )
    : basePrograms;

  const refreshWorkouts = loadSingleProgram
    ? refetchProgramWorkouts
    : refetchPrograms;

  return (
    <WorkoutsPage
      isLoading={isListLoading}
      creator={data ? data.creator : { id: influencerId }}
    >
      <ProgramPage
        tab="published"
        refreshWorkouts={refreshWorkouts}
        influencerId={influencerId}
        influencer={data ? data.creator : {}}
        programs={programs}
        workoutsLoading={isWorkoutsLoading}
        {...props}
      />
    </WorkoutsPage>
  );
}
